import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Box, Card, List, Stack, Tab, Tabs, Typography } from '@mui/material';
import { tr } from '../lib/i18n';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { AppNotification, NotificationsPage as Page } from '../api/types';
import { Button } from '../components/button';
import { Empty, ErrorState, Loading } from '../components/common';
import { NotificationItem, useNotificationActions } from '../components/notifications';

export function NotificationsPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const { markRead, markAll } = useNotificationActions();
  const list = useInfiniteQuery({
    queryKey: ['notifications', 'page', tab],
    queryFn: ({ pageParam }) => api<Page>('/notifications', { query: { cursor: pageParam, limit: 30, ...(tab === 'unread' ? { unread: 'true' } : {}) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = list.data?.pages[0]?.unreadCount ?? 0;

  const open = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.link) nav(n.link);
  };

  return (
    <Card sx={{ maxWidth: 820, width: '100%', mx: 'auto' }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
          <Tab value="all" label={tr('الكل', 'All')} />
          <Tab value="unread" label={`${tr('غير المقروءة', 'Unread')}${unread ? ` (${unread})` : ''}`} />
        </Tabs>
        <Button startIcon={<DoneAllIcon />} disabled={!unread || markAll.isPending} onClick={() => markAll.mutate()}>{tr('تعليم الكل كمقروء', 'Mark all as read')}</Button>
      </Stack>
      <Box sx={{ p: 1.5 }}>
        {list.isLoading ? (
          <Loading />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : rows.length === 0 ? (
          <Empty text={tab === 'unread' ? tr('لا توجد إشعارات غير مقروءة', 'No unread notifications') : tr('لا توجد إشعارات بعد', 'No notifications yet')} />
        ) : (
          <List disablePadding>{rows.map((n) => <NotificationItem key={n.id} n={n} onOpen={open} />)}</List>
        )}
        {list.hasNextPage && (
          <Box sx={{ textAlign: 'center', pt: 1 }}>
            <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>{tr('عرض المزيد', 'Show more')}</Button>
          </Box>
        )}
        {rows.length > 0 && <Typography variant="caption" color="text.disabled" display="block" sx={{ textAlign: 'center', mt: 1 }}>{tr('تُحذف الإشعارات المقروءة تلقائياً بعد 90 يوماً', 'Read notifications are automatically deleted after 90 days')}</Typography>}
      </Box>
    </Card>
  );
}
